import React from 'react';
import { ThemeProvider } from 'theme-ui';
import mount from './util/autoMounter';
import exportReactComponents from './legacyBridge';
import OrganizationReviews from './components/admin/OrganizationReviews';
import OrganizationMembers from './components/admin/OrganizationMembers';
import OrganizationInvitations from './components/admin/OrganizationInvitations';
import ReviewStudies from './components/admin/ReviewStudies';
import CitationImportHistory from './pages/CitationImports/CitationImportHistory/CitationImportHistory';
import ImportFromFile from './pages/CitationImports/ImportFromFile/ImportFromFile';
import DeleteReviewerWithConfirmationModal from 'components/admin/Reviewers/DeleteReviewerWithConfirmationModal';
import nest from 'util/nest';
import ExportNavigationButton from 'pages/DataExtraction/Navigation/ExportNavigationButton';
import CitationImportSource from 'pages/CitationImports/CitationImportSource';
import { SignupRoleAndStudyLevel } from 'containers/Signup/RoleAndStudyLevel/RoleAndStudyLevel';
import { ViewImportedAssignments } from 'containers/Guidelines/Triage/ViewImportedAssignments';
import { ViewHaltedStudies } from 'containers/Guidelines/Triage/ViewHaltedStudies';
import { ApolloProvider } from 'store/apollo';
import { QueryProvider } from 'query/QueryProvider';

import { theme } from 'system/theme';

import AccountCard from 'components/AccountCard';
import Avatar from 'components/Avatar';
import { Badge } from 'components/core';
import { FileUploadDialog } from 'components/FileUploadDialog';
import DropZone from 'components/FileUploadDialog/components/DropZone'; // Required so the CSS is included
import FullTextBulkUploader from 'components/FullTextBulkUploader';

import NewEffectMeasure from 'components/NewEffectMeasure';

import {
  AppFooter,
  CollaboratorExpiredModal,
  ExpiredPlanSelectionModal,
  PrimaryNavigation,
  StudiesTimedOutMessage,
} from 'components/shared';
import DataExtractionTemplate from 'pages/DataExtraction/DataExtractionTemplate/DataExtractionTemplate';
import {
  QualityAssessmentTemplate,
  QualityAssessmentDefaultTemplate,
} from 'pages/DataExtraction/QualityAssessmentTemplate';
import PrismaActions from 'pages/Prisma/components/PrismaActions/PrismaActions';

import Overview from 'containers/Organizations/Overview';
import Search from 'containers/Organizations/Search';
import Reviews from 'containers/Organizations/Reviews';
import Administrators from 'containers/Organizations/Administrators';
import Author from 'containers/Organizations/Author';
import Authors from 'containers/Organizations/Authors';
import ExportHistory from 'containers/Organizations/ExportHistory';
import ManageInvitations from 'containers/Organizations/ManageInvitations';
import ImportCRS from 'containers/Review/ImportCRS';
import { StudyScreening } from 'containers/Review/StudyScreening/StudyScreening';
import ReviewHome from 'containers/Review/Home/Home';
import ExtractionBuild from 'containers/Extraction/Build';
import ExtractionExtract from 'containers/Extraction/Extract';
import ExtractionStudyList from 'containers/Extraction/StudyListRoot';
import ExtractionCompare from 'containers/Extraction/Compare';
import ExtractionToolbarNav from 'containers/Extraction/ToolbarNav';
import { StatusBar as ExtractionStatusBar } from 'containers/Extraction/StudyList/StatusBar';
import { RefreshCachedStatus as RefreshStudiesCachedStatus } from 'containers/Extraction/StudyList/RefreshCachedStatus';
import ExportToFile from 'containers/Export/ExportToFile';
import RevmanWebExport from 'containers/Export/RevmanWebExport';
import { Show as GuidelinesShow } from 'containers/Guidelines/Show';
import { AssignToTopics } from 'containers/Guidelines/Triage/AssignToTopics';
import { AssignToQuestions } from 'containers/Guidelines/Triage/AssignToQuestions';
import { PrimaryButton } from 'system/base/Button';
import { Tags as UserTags } from 'containers/users/tags';
import { CityLookup } from 'containers/Signup/CityLookup';
import { AcceptInvitation } from 'containers/Invitations/AcceptInvitation';
import { TopicExperts } from 'containers/Guidelines/TopicExperts/TopicExperts';
import { InvitesLoader as TopicExpertInvitesLoader } from 'containers/Guidelines/TopicExperts/InvitesLoader';
import { ViewStudies } from 'containers/Guidelines/TopicExperts/ViewStudies';
import { CreateComment } from 'containers/Review/Comments/CreateComment';
import { DiscussionsLoader } from 'containers/Comments/DiscussionsLoader';
import { DiscussionLoader } from 'containers/Comments/DiscussionLoader';
import BulkUploadHistory from 'containers/BulkUploadHistory/BulkUploadHistory';
import FullTextBulkUploaderNavigationTabs from 'components/FullTextBulkUploaderNavigationTabs/FullTextBulkUploaderNavigationTabs';
import DemoAlert from 'containers/DemoAlert/DemoAlert';
import { TrialExpiryBanner } from 'containers/Review/StudyScreening/TrialExpiryBanner/TrialExpiryBanner';
import { ToastContainer } from 'components/core';

import './styles/global.scss';
import { ManageReviewersModal } from 'pages/StudyList/StudyListItem/components/ManageReviewersModal/ManageReviewersModal';
import StudyListMessage from 'pages/StudyList/StudyListMessage/StudyListMessage';
import StudyListExtractionMessage from 'pages/StudyList/StudyListExtractionMessage/StudyListExtractionMessage';
import { StudyListToolbar } from 'pages/StudyList/StudyListToolbar/StudyListToolbar';
import {
  EmptyListNoImports,
  EmptyListNoStudiesFoundInStage,
  EmptyListTrialLimit,
} from 'pages/StudyList/EmptyList/StudyListEmptyState';
import { ExtractionStatusAction } from 'pages/StudyList/ExtractionStatusAction';
import AutoExclusions from 'pages/AutoExclusions';

import AutomationNavigation from 'pages/AutoExclusions/components/AutomationNavigation';
import { EmailField } from 'components/EmailField/EmailField';

import { SamlNameFormPage } from 'components/SingleSignOn/SamlNameForm';
import { SamlDiscoveryPage } from 'components/SingleSignOn/SamlDiscoveryPage';
import { SamlEmailLinkPage } from 'components/SingleSignOn/SamlEmailLinkPage';
import UpgradeLink from 'components/UpgradeLink';
import { SkipLinks, SkipLinksWithoutFooter } from 'components/shared/SkipLinks';
import { EnvironmentAlert } from 'components/shared/EnvironmentAlert';
import PageHeader from 'components/PageHeader/PageHeader';
import {
  CommentShowPageHeader,
  CommentIndexPageHeader,
} from 'components/PageHeader/CommentsPageHeader';
import PrismaPageHeader from 'components/PageHeader/PrismaPageHeader';
import ExtractionPageHeader from 'components/PageHeader/ExtractionPageHeader';
import SummaryPageHeader from 'components/PageHeader/SummaryPageHeader';
import AuthorPageHeader from 'components/PageHeader/AuthorPageHeader';
import PageSubHeader from 'components/PageHeader/PageSubHeader';
import ExtractionPageSubHeader from 'components/PageHeader/ExtractionPageSubHeader';
import FullTextReviewPageHeader from 'components/PageHeader/FullTextReviewPageHeader';
import * as FlashMessages from 'components/shared/FlashMessage/FlashMessage';
import PrivacyAndPermissions from 'components/PrivacyAndPermissions/PrivacyAndPermissions';
import { DeleteReview } from 'pages/ReviewSettings/components/DeleteReview/DeleteReview';
import { InsightsButton } from 'containers/Organizations/Insights';
import { InsightsBanner } from 'containers/Organizations/Insights';
import ScreeningStudyListItem from 'pages/StudyList/StudyListItem/ScreeningStudyListItem';
import IrrelevantStudyListItem from 'pages/StudyList/StudyListItem/IrrelevantStudyListItem';
import ExclusionModal from 'pages/EligibilityCriteria/components/ExclusionsModal/ExclusionModal';
import FullTextReviewExcludeReasonPanel from 'pages/StudyList/StudyListItem/components/VotePanel/FullTextReview/FullTextReviewExcludeReasonPanel';
import StageDetail from 'containers/Review/Home/StageDetail';
import { ProfilePage } from 'pages/ProfilePage/ProfilePage';
import { LibKey } from 'pages/LibKey/LibKey';
import { ExtractorActions } from 'pages/StudyList/StudyListItem/ExtractorActions';
import { AutomationPromoBanner } from 'pages/StudyList/AutomationPromoBanner/AutomationPromoBanner';
import { ScreeningStudyListPage } from 'pages/StudyList/ScreeningStudyListPage/ScreeningStudyListPage';

const WithTheme = (props: any) =>
  React.createElement(ThemeProvider, { theme, ...props });

mount({
  ...FlashMessages,
  EnvironmentAlert,
  SamlNameFormPage,
  SamlDiscoveryPage,
  SamlEmailLinkPage,
  AutomationNavigation,
  StudyListMessage,
  StudyListExtractionMessage,
  StudyListToolbar,
  EmptyListNoImports,
  EmptyListNoStudiesFoundInStage,
  EmptyListTrialLimit,
  ExtractionStatusAction,
  StudiesTimedOutMessage,
  ToastContainer,
  PrismaActions: PrismaActions,
  CitationImportSource: CitationImportSource,
  ImportFromFile: ImportFromFile,
  CitationImportHistoryPage: CitationImportHistory,
  DataExtractionTemplatePage: DataExtractionTemplate,
  QualityAssessmentTemplatePage: QualityAssessmentTemplate,
  QualityAssessmentDefaultTemplatePage: QualityAssessmentDefaultTemplate,
  ExportNavigationButton: ExportNavigationButton,
  PageHeader: nest(WithTheme, PageHeader),
  CommentShowPageHeader: nest(WithTheme, CommentShowPageHeader),
  CommentIndexPageHeader: nest(WithTheme, CommentIndexPageHeader),
  PageSubHeader: nest(WithTheme, PageSubHeader),
  PrismaPageHeader: nest(WithTheme, PrismaPageHeader),
  ExtractionPageHeader: nest(WithTheme, ExtractionPageHeader),
  FullTextReviewPageHeader: nest(WithTheme, FullTextReviewPageHeader),
  SummaryPageHeader: nest(WithTheme, SummaryPageHeader),
  AuthorPageHeader: nest(WithTheme, AuthorPageHeader),
  ExtractionPageSubHeader: ExtractionPageSubHeader,
  AdminOrganizationReviews: nest(
    WithTheme,
    ApolloProvider,
    OrganizationReviews
  ),
  AdminOrganizationMembers: nest(
    WithTheme,
    ApolloProvider,
    OrganizationMembers
  ),
  AdminOrganizationInvitations: nest(
    WithTheme,
    ApolloProvider,
    OrganizationInvitations
  ),
  DeleteReviewerWithConfirmationModal: DeleteReviewerWithConfirmationModal,
  AdminReviewStudies: nest(WithTheme, ApolloProvider, ReviewStudies),
  Badge: Badge,
  BulkUploadHistory: nest(WithTheme, BulkUploadHistory),
  AccountCard: nest(WithTheme, AccountCard),
  Avatar: nest(WithTheme, Avatar),
  DeleteReview,
  DemoAlert,
  FileUploadDialog: nest(WithTheme, FileUploadDialog),
  DropZone: nest(WithTheme, DropZone),
  FullTextBulkUploader: nest(WithTheme, ApolloProvider, FullTextBulkUploader),
  FullTextBulkUploaderNavigationTabs: nest(
    WithTheme,
    FullTextBulkUploaderNavigationTabs
  ),
  PrimaryNavigation: PrimaryNavigation,
  AppFooter: AppFooter,
  CollaboratorExpiredModal: nest(
    WithTheme,
    ApolloProvider,
    CollaboratorExpiredModal
  ),
  ExpiredPlanSelectionModal: ExpiredPlanSelectionModal,
  PrivacyAndPermissions: nest(WithTheme, ApolloProvider, PrivacyAndPermissions),
  OrganizationsOverview: nest(WithTheme, ApolloProvider, Overview),
  InsightsButton,
  InsightsBanner,
  ManageReviewersModal,
  OrganizationSearch: nest(WithTheme, ApolloProvider, Search),
  Reviews: nest(WithTheme, ApolloProvider, Reviews),
  Administrators: nest(WithTheme, ApolloProvider, Administrators),
  Authors: nest(WithTheme, ApolloProvider, Authors),
  Author: nest(WithTheme, ApolloProvider, Author),
  ExportHistory: nest(WithTheme, ApolloProvider, ExportHistory),
  ManageInvitations: nest(WithTheme, ApolloProvider, ManageInvitations),
  ImportCRS: nest(WithTheme, ApolloProvider, ImportCRS),
  StudyScreening: nest(WithTheme, ApolloProvider, StudyScreening),
  ExtractionBuild: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ExtractionBuild
  ),
  ExtractionExtract: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ExtractionExtract
  ),
  ExtractionStatusBar: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ExtractionStatusBar
  ),
  RefreshStudiesCachedStatus: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    RefreshStudiesCachedStatus
  ),
  ExtractionStudyList: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ExtractionStudyList
  ),
  ExtractionCompare: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ExtractionCompare
  ),
  ExtractionToolbarNav: ExtractionToolbarNav,
  GuidelinesShow: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    GuidelinesShow
  ),
  GuidelinesTriageAssignToTopics: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    AssignToTopics
  ),
  GuidelinesTriageAssignToQuestions: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    AssignToQuestions
  ),
  GuidelinesTriageViewImportedAssignments: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ViewImportedAssignments
  ),
  GuidelinesTriageViewHaltedStudies: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ViewHaltedStudies
  ),
  GuidelinesTopicExperts: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    TopicExperts
  ),
  GuidelinesInvites: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    TopicExpertInvitesLoader
  ),
  GuidelinesTopicExpertsStudies: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    ViewStudies
  ),
  CreateComment: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    CreateComment
  ),
  DiscussionsLoader: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    DiscussionsLoader
  ),
  DiscussionLoader: nest(
    WithTheme,
    ApolloProvider,
    React.StrictMode,
    DiscussionLoader
  ),
  ExportToFile: nest(WithTheme, React.StrictMode, ExportToFile),
  RevmanWebExport: nest(WithTheme, React.StrictMode, RevmanWebExport),
  ReviewHome: nest(
    WithTheme,
    ApolloProvider,
    QueryProvider,
    React.StrictMode,
    ReviewHome
  ),
  PrimaryButton: PrimaryButton,
  CityLookup: nest(WithTheme, CityLookup),
  SignupRoleAndStudyLevel: nest(WithTheme, SignupRoleAndStudyLevel),
  UserTags: nest(WithTheme, UserTags),
  AcceptInvitation: nest(WithTheme, AcceptInvitation),
  TrialExpiryBanner: nest(WithTheme, ApolloProvider, TrialExpiryBanner),
  AutoExclusions: AutoExclusions,
  EmailField,
  UpgradeLink,
  SkipLinks,
  SkipLinksWithoutFooter,
  ScreeningStudyListItem,
  IrrelevantStudyListItem,
  ExclusionModal,
  FullTextReviewExcludeReasonPanel,
  StageDetail,
  ProfilePage,
  LibKey,
  ExtractorActions,
  AutomationPromoBanner,
  ScreeningStudyListPage,
});

exportReactComponents({
  NewEffectMeasure: nest(WithTheme, ApolloProvider, NewEffectMeasure),
});
